import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Truck, CheckCircle, Search, XCircle, Eye, MapPin, Banknote } from 'lucide-react';
import { supabase } from '../services/supabase';
import TitularCuenta from '../components/TitularCuenta';
import type { Database } from '../types/database.types';
import type { OrderStatus, PrepStatus } from '../types/database';
import { cobroDe } from '../types/database';
import Modal from '../components/ui/Modal';
import { listaEnCastellano } from '../lib/leerVarias';
import { etiquetaDeLaFormaDePago } from '../lib/formasDePago';

// Los dos mapas auxiliares del listado, nombrados para el aviso.
const QUE_CODIGOS = 'los códigos de descuento';
const QUE_NOMBRES = 'los nombres de quién regaló y quién confirmó cada cobro';

type OrderRow = Database['public']['Tables']['orders']['Row'];

// Embed que pide `loadOrders`: orders + datos del cliente vía la FK
// `orders.user_id → profiles.id`. El cliente Supabase no infiere la columna
// como relación 1:1, así que el doble cast vía `unknown` es necesario
// (mismo patrón que la 5.4a).
//
// La FK va NOMBRADA (`profiles!user_id`) y no se puede volver a abreviar: desde
// la mig 199 hay DOS caminos de `orders` a `profiles` (`user_id`, el alumno al
// que va el pedido, y `paid_by_profile_id`, el tutor que lo pagó). Con el embed
// a secas PostgREST no elige: responde 300 PGRST201 y la consulta entera se cae.
// Aquí manda `user_id`, que es el "Alumno" que pinta la tabla.
type OrderRowWithProfile = OrderRow & {
  profiles: { full_name: string; email: string; phone: string | null } | null;
};

// `orders.items` se almacena como JSONB (Json en el generado). El admin solo
// lee claves conocidas. Dos formatos conviven:
//   - Legacy (pre-T1): { name, qty, price_cents } — price_cents es UNITARIO.
//   - T1+T2: { name, quantity, kind, fulfillment_type, base_cents, final_cents,
//             discount_cents, applied_code_id } — *_cents son TOTALES de línea.
// PR-T2.1.3 Bug 1: el render leía `price_cents` (ausente en T1+T2) → precio por
// línea roto y descuentos invisibles.
type OrderItem = {
  product_id?: string;
  name: string;
  qty?: number;
  quantity?: number;
  size?: string | null;
  color?: string | null;
  kind?: 'session_pack' | 'product';
  fulfillment_type?: string;
  price_cents?: number; // legacy (unitario)
  base_cents?: number; // T1+T2 (total línea, antes de descuento)
  final_cents?: number; // T1+T2 (total línea, tras descuento)
  discount_cents?: number; // T1+T2
  applied_code_id?: string | null; // T1+T2
  // Combinado v2: lo que lleva dentro, ya resuelto por el checkout. Se pinta
  // porque SIN ESTO un pack con ropa llega aquí sin ninguna talla a la vista y
  // no hay forma de saber qué meter en la caja.
  components?: unknown;
};

/** Un componente del combinado, tal y como lo sella el checkout (formato v2). */
type OrderComponent = {
  kind?: string;
  name?: string;
  quantity?: number;
  size?: string | null;
  color?: string | null;
  variant_label?: string | null;
};

/** Los componentes de un combinado v2, o lista vacía si no lo es. */
function componentesV2(item: OrderItem): OrderComponent[] {
  const c = item.components;
  if (!c || typeof c !== 'object' || Array.isArray(c)) return [];
  const obj = c as { v?: unknown; items?: unknown };
  if (Number(obj.v) !== 2 || !Array.isArray(obj.items)) return [];
  return obj.items as OrderComponent[];
}

// Economía de una línea, tolerante a legacy y T1+T2. Nunca produce NaN.
function lineEconomics(item: OrderItem) {
  const qty = item.quantity ?? item.qty ?? 1;
  // base = total de línea antes de descuento. Legacy: price_cents es unitario.
  const lineBase =
    item.base_cents ??
    (item.price_cents != null ? item.price_cents * qty : (item.final_cents ?? 0));
  const lineFinal = item.final_cents ?? lineBase;
  const discount = item.discount_cents ?? Math.max(0, lineBase - lineFinal);
  const unit = qty > 0 ? lineBase / qty : lineBase;
  return { qty, unit, lineBase, lineFinal, discount };
}

const FULFILLMENT_LABEL: Record<string, string> = {
  physical_stock: 'Producto físico',
  membership: 'Matrícula',
  subscription: 'Suscripción',
  service: 'Vídeo análisis',
  bundle: 'Pack combinado',
};
function itemTypeLabel(item: OrderItem): string | null {
  if (item.kind === 'session_pack') return 'Bono';
  if (item.fulfillment_type) return FULFILLMENT_LABEL[item.fulfillment_type] ?? null;
  return null;
}

// `orders.shipping_address` también es Json. Fallback a JSON.stringify en
// el modal cuando no encaja con el shape conocido.
type ShippingAddress = {
  street?: string;
  city?: string;
  postal_code?: string;
  country?: string;
};

interface Order {
  id: string;
  // [276] El número corto, el que la familia escribe en el concepto de una
  // transferencia y el que dirección busca aquí. Es un entero en la base; el
  // `VTX-` y el relleno a cuatro son presentación.
  order_number: number;
  // Nullable desde la mig 207: un pedido sobrevive al borrado de su comprador
  // (obligación contable) y entonces el nombre vive en `billing_name`.
  user_id: string | null;
  billing_name: string | null;
  total_cents: number;
  status: OrderStatus;
  // [259] El eje de PREPARACIÓN. `status` decía a la vez "el candado está
  // echado" y "hay ropa que preparar", así que el botón de preparar escribía la
  // palabra del cobro. Ahora son dos columnas y esta página solo escribe esta.
  prep_status: PrepStatus;
  created_at: string;
  updated_at: string;
  items: OrderItem[];
  shipping_address: ShippingAddress | null;
  // [229] Motivo por el que un pedido PAGADO no llegó a conceder nada. NULL es
  // lo normal. Un pedido cobrado y sin conceder tiene que verse sin tener que
  // preguntar, así que se pinta en la lista y en el detalle.
  fulfillment_error: string | null;
  // Lo escribe SOLO `fulfill_order`, desde el webhook de Stripe. Es la única
  // señal en toda la tabla de que por este pedido pasó dinero de verdad.
  stripe_payment_intent_id: string | null;
  // [248] Quién lo regaló y por qué. NULL = no es un regalo del club: o se
  // pagó, o es un canje gratuito de código. Es lo que separa dos pedidos a
  // 0 € que hasta ahora se veían idénticos.
  gifted_by: string | null;
  gift_reason: string | null;
  // [275] Cómo se cobró, en una columna y no en el prefijo de un intent. Nace
  // con el pedido —el checkout ya sabe si va a Stripe o si alguien lo pagará en
  // mano— y desde aquí solo la lee: escribirla es cosa de la RPC.
  payment_method: string;
  // El número de la transferencia, o lo que dirección anotó al confirmar.
  payment_reference: string | null;
  // Quién dio por bueno el cobro en mano, y cuándo. NULL en todo lo demás.
  paid_confirmed_by: string | null;
  paid_confirmed_at: string | null;
  profile?: { full_name: string; email: string; phone: string | null };
}

// [275] Las cuatro formas de cobrar en mano. Es la MISMA lista que el CHECK
// `orders_payment_method_check` y que la RPC: si un día crece, crece en los tres
// sitios. Las otras tres formas —`stripe`, `regalo`, `gratis`— no salen aquí
// porque no hay nada que cobrar: una la cobra la pasarela y las otras dos no se
// cobran.
const MANUAL_METHODS = ['efectivo', 'transferencia', 'bizum', 'otro'];

/**
 * El número de pedido tal y como se enseña y se dice: `VTX-0042`.
 *
 * El relleno a cuatro es cosmético, no un tope: el pedido 10000 se pinta con
 * cinco dígitos y sigue siendo válido.
 */
function numeroDePedido(n: number): string {
  return `VTX-${String(n).padStart(4, '0')}`;
}

/**
 * Lo que se busca cuando lo tecleado son dígitos.
 *
 * Dirección va a copiar el número del concepto de una transferencia, y ahí
 * puede venir como `VTX-0042`, `vtx 42`, `Pedido 42` o `42`. Se queda con los
 * dígitos y se compara como número, así que las cuatro formas encuentran lo
 * mismo y los ceros a la izquierda no estorban. Si no queda ningún dígito, no
 * es una búsqueda por número y no se estropea la de siempre, por nombre.
 */
function numeroBuscado(q: string): number | null {
  const digitos = q.replace(/\D/g, '');
  return digitos === '' ? null : Number(digitos);
}

/** ¿Este pedido se cobra en mano? */
function enMano(o: { payment_method: string | null }): boolean {
  return MANUAL_METHODS.includes(o.payment_method ?? '');
}

// Lo que dirección tiene que cobrar. Se exige 'pending' además de la forma de
// pago porque es EXACTAMENTE el candado de la RPC (`WHERE status = 'pending'`):
// así el botón solo aparece donde la confirmación puede ganar la fila, y no
// sobre uno que otro admin acaba de cobrar.
//
// No hace falta mirar 'expired': desde la 275 el cron `expire_unpaid_orders`
// solo barre `payment_method = 'stripe'`, así que un pedido en mano no caduca.
function pendienteDeCobroEnMano(o: {
  status: OrderStatus;
  payment_method: string | null;
}): boolean {
  return enMano(o) && o.status === 'pending';
}

// Un pedido sin pagar. `pending` sin intent es un carrito abandonado —la fila
// nace al pulsar "Pagar", ANTES de ir a Stripe— y `expired` es ese mismo
// carrito ya caducado por el cron de la mig 240.
//
// Se mira el intent y no solo el estado a propósito: mientras el pedido esté
// en 'pending' con intent, algo pasó por `fulfill_order` y no es un abandono.
//
// [275] Y un cobro en mano NO es un abandono, por mucho que se le parezca desde
// la base: mismo 'pending', mismo intent vacío. La diferencia es que aquí sí hay
// alguien que quiere pagar y a quien el club tiene que cobrar. Sin esta salida
// el pedido se apartaba de la vista por defecto, perdía el botón de cancelar y
// el detalle le decía a dirección que no lo tocara — justo lo contrario de lo
// que hay que hacer con él.
// [317] Lo que `admin_order_grants` / `admin_revoke_order_grants` devuelven. La
// misma forma las dos: el previo es la misma función con `aplicar` a false, para
// que la lista que se enseña no pueda ser otra que la que se aplica.
interface Grants {
  ok?: boolean;
  error?: string;
  order_number?: number;
  suscripciones?: { id: string; status: string; inscripciones: number }[];
  bonos?: { id: string; credit_type: string; restantes: number; gastadas: number }[];
  creditos_video?: { id: string; retirados: number }[];
  stock?: { variant_id: string; unidades: number }[];
  // [328] Lo que NO vuelve al inventario porque ya se entregó. Lo decide la
  // función y la pantalla lo pinta: NO se vuelve a derivar aquí a partir de
  // `envio`, que es el defecto que arregló la 327 — dos sitios decidiendo lo
  // mismo acaban discrepando, y manda el que no se ve.
  material_no_devuelto?: { variant_id: string; unidades: number }[];
  descuentos_liberados?: number;
  envio?: string;
  matricula?: string;
}

// Los rechazos de la RPC, dichos con las palabras de quien lo está leyendo.
function motivoDelRechazo(res: Grants, o: Order | null): string {
  switch (res.error) {
    case 'not_authorized':
      return 'No tienes permiso para revocar lo concedido por un pedido.';
    case 'order_not_found':
      return 'Ese pedido ya no existe.';
    case 'estado_no_revocable':
      return `Un pedido en "${o?.status ?? '—'}" no pasa por aquí: solo se revoca lo que concedió un pedido cobrado.`;
    // [328] La base ya no devuelve esto: un pedido entregado se revoca, y el
    // material que no vuelve se dice en la lista. La rama SE QUEDA a propósito,
    // porque hay una ventana en la que sí puede llegar: el panel sale con el
    // deploy de Vercel al mergear, y la migración la aplica una persona después.
    // En ese hueco, un panel nuevo habla con una base vieja, y es mejor que diga
    // una frase que un código crudo.
    case 'preparacion_ya_salida':
      return 'El envío ya salió, y esta base todavía no deja revocar en ese caso. Falta aplicar la migración 328.';
    default:
      return `No se pudo revocar: ${res.error ?? 'respuesta inesperada'}`;
  }
}

function sinPagar(o: {
  status: OrderStatus;
  stripe_payment_intent_id: string | null;
  payment_method: string | null;
}): boolean {
  if (enMano(o)) return false;
  return !o.stripe_payment_intent_id && (o.status === 'pending' || o.status === 'expired');
}

// `pending` NO es "Recibido". Nadie ha recibido nada: la persona pulsó "Pagar"
// y no llegó a pagar. Llamarlo "Recibido" es lo que hacía que un carrito
// abandonado se leyera como un pedido que hay que preparar.
//
// [259] `shipped` y `delivered` siguen en el mapa y ya no los pinta nadie: la
// lista los colapsa a "Procesado" con `cobroDe()` y el desplegable los filtra.
// Se quedan porque en la FASE 1 la app instalada todavía puede dejar un pedido
// ahí, y un `?? o.status` que pintara el literal en crudo sería peor. Salen en
// la fase 2, con el CHECK.
const STATUS_LABELS: Record<string, string> = {
  pending: 'Sin pagar',
  paid: 'Procesado',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado',
  expired: 'Sin pagar · caducado',
};
const STATUS_BADGE: Record<string, string> = {
  // Gris, no ámbar: un pedido sin pagar no es una tarea pendiente del club.
  pending: 'badge-silver',
  paid: 'badge-gold',
  shipped: 'badge-silver',
  delivered: 'badge-success',
  cancelled: 'badge-error',
  expired: 'badge-silver',
};

// [259] Las etiquetas de la PREPARACIÓN. `sin_cobrar` y `no_aplica` no se
// pintan: el primero no dice nada que el estado de cobro no diga ya, y el
// segundo es la mayoría de los pedidos —bonos, matrícula, suscripciones— que
// nunca han necesitado que nadie prepare nada. Pintarlos llenaría la tabla de
// insignias que no piden ninguna acción.
const PREP_LABELS: Record<PrepStatus, string> = {
  sin_cobrar: '',
  no_aplica: '',
  pendiente: 'Por preparar',
  enviado: 'Enviado',
  entregado: 'Entregado',
};
const PREP_BADGE: Record<PrepStatus, string> = {
  sin_cobrar: 'badge-silver',
  no_aplica: 'badge-silver',
  // Ámbar: esto SÍ es una tarea pendiente del club, al revés que un carrito
  // sin pagar.
  pendiente: 'badge-gold',
  enviado: 'badge-silver',
  entregado: 'badge-success',
};

// [259] El aviso al cliente cuelga ahora de la preparación.
//
// Ya no hay ningún aviso al pasar a 'paid': ese lo mandaba el botón "Procesar",
// que era el candado disfrazado y ha desaparecido. El pedido llega a 'paid' por
// `fulfill_order`, que ya avisa por su cuenta — el botón añadía un segundo
// aviso por el mismo cobro.
const PREP_NOTIF: Record<PrepStatus, string> = {
  sin_cobrar: '',
  no_aplica: '',
  pendiente: '',
  enviado: 'Tu pedido ha sido enviado. Pronto llegará a tu dirección.',
  entregado: '¡Tu pedido ha sido entregado! Esperamos que te guste.',
};

const STATUS_NOTIF: Record<string, string> = {
  cancelled: 'Tu pedido ha sido cancelado.',
};

// Los `ok:false` de `admin_confirm_manual_payment`, en castellano. Se enumeran
// uno a uno en vez de pintar el código crudo: quien está delante tiene que saber
// qué hacer a continuación, y 'no_es_cobro_en_mano' no se lo dice.
function mensajeDeFallo(res: { error?: string; status?: string; detalle?: string }): string {
  switch (res.error) {
    case 'pedido_inexistente':
      return 'Ese pedido ya no existe.';
    case 'no_es_cobro_en_mano':
      return 'Este pedido no se cobra en mano, así que aquí no hay nada que confirmar.';
    case 'referencia_obligatoria':
      return 'Con "Otro" hay que anotar la referencia: sin ella no se sabe qué se cobró.';
    case 'ya_no_estaba_pendiente':
      return `Alguien se te adelantó: el pedido ya está en "${
        STATUS_LABELS[res.status ?? ''] ?? res.status
      }". No se ha cobrado dos veces.`;
    case 'reparto_fallido':
      // El pedido ha vuelto a 'pending' él solo: el candado de la RPC va DENTRO
      // del bloque justo para esto. Así que se puede volver a intentar, y hay
      // que decirlo — si no, esto se lee como un cobro perdido.
      return `El cobro NO se ha dado por bueno: al conceder lo comprado falló algo y el pedido sigue pendiente. Se puede arreglar y volver a intentarlo. ${
        res.detalle ?? ''
      }`;
    default:
      return `No se pudo confirmar el cobro: ${res.error ?? 'error desconocido'}`;
  }
}

export default function PedidosPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  // Cambiar el estado de un pedido es una acción de fila y además avisa al
  // cliente: necesita un sitio donde contar que algo no ha entrado.
  const [listError, setListError] = useState('');
  // Los dos mapas auxiliares que el embed no trae. Su `try/catch` no se
  // ejecuta nunca —postgrest-js no rechaza: devuelve `{ data: null, error }`
  // mientras `shouldThrowOnError` sea false—, así que el fallo se colaba como
  // mapa vacío y el pedido se pintaba con "alguien del club" y el descuento
  // sin código. Best-effort sigue siendo lo correcto (un pedido cobrado se
  // tiene que ver), pero se dice qué falta.
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);
  // Distingue "no hay pedidos" de "no he podido leerlos". Sin esta distinción
  // las dos cosas se pintaban igual y la segunda pasaba por la primera.
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  // Los intentos sin pagar quedan fuera de la vista por defecto. No se ocultan
  // del todo —desde la base, un `pending` sin intent es indistinguible de un
  // pago cuyo webhook no llegó, y esconderlos es justo lo que nos mordió con
  // el 4922981b—, pero tampoco se mezclan con lo que hay que preparar.
  const [verSinPagar, setVerSinPagar] = useState(false);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);
  // PR-T2.1.3 Bug 1: mapa id→código para mostrar el código de descuento aplicado
  // (los items solo guardan `applied_code_id`).
  const [codeMap, setCodeMap] = useState<Record<string, string>>({});
  // [248] id → nombre. Empezó siendo quien regaló cada regalo y desde la 275
  // lleva también a quien confirmó cada cobro en mano. Va en un mapa aparte y NO
  // en un segundo embed a propósito: `orders` tiene ya CUATRO claves ajenas a
  // `profiles` —la cuarta es `paid_confirmed_by`— y un embed sin desambiguar es
  // el 300 que dejó la lista en "Sin pedidos" con los pedidos ahí dentro
  // (#496). Mismo patrón que el mapa de códigos.
  const [nameMap, setNameMap] = useState<Record<string, string>>({});
  // [275] El pedido cuyo cobro en mano se está confirmando, con su referencia.
  // `confirmBusy` no es cosmético: sin él, dos clics seguidos mandan dos veces
  // la RPC, y aunque el candado impida cobrar dos veces, el segundo vuelve con
  // un "alguien se te adelantó" que confunde a quien acaba de pulsar.
  // [317] Revocar lo concedido. Anular un pedido PAGADO ya no es un cambio de
  // estado: la base lo rechaza (orders_admin_solo_avanza) porque un UPDATE de
  // `status` no devuelve nada de lo que ese pago concedió. Así quedó el pedido
  // 513 —210 €, anulado— con la suscripción puesta, dos unidades fuera del
  // inventario y un envío en cola. El paso previo enseña la lista.
  const [revokeOrder, setRevokeOrder] = useState<Order | null>(null);
  const [revokeGrants, setRevokeGrants] = useState<Grants | null>(null);
  const [revokeMotivo, setRevokeMotivo] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState('');
  const [confirmOrder, setConfirmOrder] = useState<Order | null>(null);
  const [confirmRef, setConfirmRef] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  // Un cobro que entra no puede pasar en silencio: es la única acción de esta
  // página que mueve dinero y concede lo comprado.
  const [flash, setFlash] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadOrders();
  }, []);

  // `?pedido=<id>` abre el detalle de ese pedido. Lo usa el historial de
  // inventario, que enlaza cada venta con el pedido que la causó: sin esto el
  // enlace dejaría al operador en el listado, buscando a mano el pedido que
  // acaba de pinchar. El parámetro se consume una vez —se limpia de la URL—
  // para que cerrar el modal no lo vuelva a abrir.
  useEffect(() => {
    const id = searchParams.get('pedido');
    if (!id || orders.length === 0) return;
    const encontrado = orders.find((o) => o.id === id);
    if (encontrado) setDetailOrder(encontrado);
    searchParams.delete('pedido');
    setSearchParams(searchParams, { replace: true });
  }, [orders, searchParams, setSearchParams]);

  async function loadOrders() {
    const { data, error } = await supabase
      .from('orders')
      .select('*, profiles!user_id(full_name, email, phone)')
      .order('created_at', { ascending: false });
    // Una lectura que falla NO es una lista vacía. Sin esto, el 300 del embed
    // ambiguo dejaba `data` en null y la tabla pintaba "Sin pedidos": un pedido
    // cobrado desaparecía del panel sin que nadie tuviera nada que mirar.
    if (error) {
      setListError(`No se pudieron cargar los pedidos: ${error.message}`);
      setLoadFailed(true);
      setOrders([]);
      setLoading(false);
      return;
    }
    setListError('');
    setLoadFailed(false);
    // Códigos de descuento (best-effort): un fallo aquí NO debe romper el
    // listado de pedidos. Solo sirve para mostrar el código junto al descuento.
    const fallidos: string[] = [];
    const { data: codes, error: errCodes } = await supabase
      .from('discount_codes')
      .select('id, code');
    if (errCodes) {
      // mapa vacío → el descuento se muestra sin el código
      fallidos.push(QUE_CODIGOS);
    }
    setCodeMap(
      Object.fromEntries(
        ((codes ?? []) as { id: string; code: string }[]).map((c) => [c.id, c.code])
      )
    );

    // Los nombres que la tabla necesita y el embed no trae: quién regaló cada
    // regalo y, desde la 275, quién dio por bueno cada cobro en mano.
    // Best-effort, igual que los códigos: si falla, el pedido se sigue viendo
    // —como regalo, o como cobrado— solo que sin nombre.
    const perfilIds = [
      ...new Set(
        ((data ?? []) as unknown as OrderRowWithProfile[])
          .flatMap((o) => [o.gifted_by, o.paid_confirmed_by])
          .filter((v): v is string => !!v)
      ),
    ];
    if (perfilIds.length > 0) {
      const { data: gifters, error: errGifters } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', perfilIds);
      if (errGifters) {
        // mapa vacío → se ve "Regalo" sin el nombre de quien lo dio, y el cobro
        // confirmado sin el nombre de quien lo confirmó. Los dos nombres son
        // trazabilidad: quién regaló, y quién dio por bueno un cobro en mano.
        fallidos.push(QUE_NOMBRES);
      }
      setNameMap(
        Object.fromEntries(
          ((gifters ?? []) as { id: string; full_name: string | null }[]).map((g) => [
            g.id,
            g.full_name ?? 'Alguien del club',
          ])
        )
      );
    }
    setBloquesFallidos(fallidos);
    const rows = (data ?? []) as unknown as OrderRowWithProfile[];
    setOrders(
      rows.map<Order>((o) => ({
        id: o.id,
        order_number: o.order_number,
        user_id: o.user_id,
        billing_name: o.billing_name,
        total_cents: o.total_cents,
        status: o.status as OrderStatus,
        prep_status: o.prep_status as PrepStatus,
        created_at: o.created_at,
        updated_at: o.updated_at,
        items: (o.items as unknown as OrderItem[]) ?? [],
        shipping_address: o.shipping_address as unknown as ShippingAddress | null,
        fulfillment_error: o.fulfillment_error ?? null,
        stripe_payment_intent_id: o.stripe_payment_intent_id ?? null,
        gifted_by: o.gifted_by ?? null,
        gift_reason: o.gift_reason ?? null,
        // El `?? 'stripe'` no es un default de negocio: desde la 275 la columna
        // es NOT NULL y siempre llega. Cubre solo el caso de leer una base sin
        // la migración aplicada, y ahí tratarlo como tarjeta es lo prudente —
        // no ofrece confirmar un cobro que esa base no sabría registrar.
        payment_method: o.payment_method ?? 'stripe',
        payment_reference: o.payment_reference ?? null,
        paid_confirmed_by: o.paid_confirmed_by ?? null,
        paid_confirmed_at: o.paid_confirmed_at ?? null,
        profile: o.profiles ?? undefined,
      }))
    );
    setLoading(false);
  }

  // [259] Avanzar la PREPARACIÓN. Es la gemela de `updateStatus` y comparte su
  // regla más cara: si la escritura no entra, no se avisa a nadie. Lo que NO
  // comparte es la columna — y esa es la razón de que exista. Escribir
  // 'paid' desde aquí quemaba el candado de `fulfill_order`.
  async function updatePrep(order: Order, newPrep: PrepStatus) {
    const { error } = await supabase
      .from('orders')
      .update({ prep_status: newPrep })
      .eq('id', order.id);
    if (error) {
      setListError(`No se pudo cambiar la preparación del pedido: ${error.message}`);
      return;
    }
    setListError('');
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, prep_status: newPrep } : o)));
    if (detailOrder?.id === order.id) setDetailOrder({ ...detailOrder, prep_status: newPrep });

    const notifBody = PREP_NOTIF[newPrep];
    if (notifBody) {
      const { error: notifError } = await supabase.from('notifications').insert({
        user_id: order.user_id,
        title: `Pedido ${PREP_LABELS[newPrep]}`,
        body: notifBody,
        type: 'purchase',
        data: { order_id: order.id },
      });
      if (notifError) {
        setListError(
          `El pedido pasó a "${PREP_LABELS[newPrep]}", pero no se pudo avisar al cliente: ${notifError.message}`
        );
      }
    }
  }

  // [317] Abre el paso previo: qué se va a revocar, antes de revocarlo. La
  // lista NO se construye aquí: la da la misma función que revoca, con
  // `aplicar` a false. Calcularla en la pantalla permitiría enseñar una cosa y
  // aplicar otra.
  async function abrirRevocacion(o: Order) {
    setDetailOrder(null);
    setRevokeOrder(o);
    setRevokeGrants(null);
    setRevokeMotivo('');
    setRevokeError('');
    const { data, error } = await supabase.rpc('admin_order_grants', { p_order_id: o.id });
    if (error) {
      setRevokeError(`No se pudo leer lo que concedió el pedido: ${error.message}`);
      return;
    }
    const res = (data ?? {}) as Grants;
    if (!res.ok) {
      setRevokeError(motivoDelRechazo(res, o));
      return;
    }
    setRevokeGrants(res);
  }

  async function revocar() {
    if (!revokeOrder) return;
    setRevokeBusy(true);
    setRevokeError('');
    const { data, error } = await supabase.rpc('admin_revoke_order_grants', {
      p_order_id: revokeOrder.id,
      p_motivo: revokeMotivo.trim() || null,
    });
    setRevokeBusy(false);
    if (error) {
      setRevokeError(`No se pudo revocar: ${error.message}`);
      return;
    }
    const res = (data ?? {}) as Grants;
    if (!res.ok) {
      setRevokeError(motivoDelRechazo(res, revokeOrder));
      // La lista se recarga igual: si el rechazo es porque otro lo movió
      // mientras esta pantalla miraba, dejar la fila como estaba repetiría la
      // mentira.
      await loadOrders();
      return;
    }
    setRevokeOrder(null);
    setRevokeGrants(null);
    setListError('');
    await loadOrders();
  }

  async function updateStatus(order: Order, newStatus: OrderStatus) {
    // [317] Un pedido COBRADO no se anula con un cambio de estado. La base lo
    // rechazaría de todas formas; se para aquí para no enseñar un error de
    // Postgres a quien solo ha pulsado un botón.
    if (newStatus === 'cancelled' && cobroDe(order.status) === 'paid') {
      await abrirRevocacion(order);
      return;
    }
    const { error } = await supabase
      .from('orders')
      .update({ status: newStatus })
      .eq('id', order.id);
    // Esto era lo más caro de la página: si el cambio de estado no entraba
    // pero la notificación sí, al cliente le llegaba un "Pedido enviado" de un
    // pedido que seguía sin enviar. Si el estado no cambia, no se avisa a
    // nadie y la pantalla no miente.
    if (error) {
      setListError(`No se pudo cambiar el estado del pedido: ${error.message}`);
      return;
    }
    setListError('');
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)));
    if (detailOrder?.id === order.id) setDetailOrder({ ...detailOrder, status: newStatus });

    const notifBody = STATUS_NOTIF[newStatus];
    if (notifBody) {
      const { error: notifError } = await supabase.from('notifications').insert({
        user_id: order.user_id,
        title: `Pedido ${STATUS_LABELS[newStatus]}`,
        body: notifBody,
        type: 'purchase',
        data: { order_id: order.id },
      });
      // El pedido SÍ cambió de estado: no se revierte. Pero el cliente no se
      // ha enterado, y quien lo gestiona tiene que saberlo.
      if (notifError) {
        setListError(
          `El pedido pasó a "${STATUS_LABELS[newStatus]}", pero no se pudo avisar al cliente: ${notifError.message}`
        );
      }
    }
  }

  // [275] Abrir la confirmación CIERRA el detalle. Son dos `Modal` y los dos
  // pintan un `role="dialog"`: abiertos a la vez, ni un lector de pantalla ni un
  // test pueden decir cuál de los dos "Cancelar" es cuál.
  function abrirConfirmacion(o: Order) {
    setDetailOrder(null);
    setConfirmOrder(o);
    // La referencia que ya hubiera, si es un segundo intento tras un reparto
    // fallido: no hay por qué hacer que la escriban otra vez.
    setConfirmRef(o.payment_reference ?? '');
    setConfirmError('');
    setFlash('');
  }

  // [275] Dirección da por bueno un cobro en mano.
  //
  // Todo pasa por la RPC y NADA por un UPDATE, y no es una preferencia de
  // estilo: las cuatro columnas del cobro están congeladas por el trigger de la
  // 262 —que la 275 amplió de 16 a 20 columnas—, así que un
  // `orders.update({ payment_reference })` desde aquí no es que esté mal visto,
  // es que revienta con un check_violation. Y `pending -> paid` tampoco está en
  // la lista de transiciones que el panel puede escribir: hacerlo a mano
  // quemaría el candado del reparto, que es exactamente lo que hacía el viejo
  // botón "Procesar" que la 259 quitó.
  async function confirmarCobro() {
    if (!confirmOrder) return;
    const ref = confirmRef.trim();
    // El CHECK de la tabla y la RPC lo exigen las dos. Aquí se dice antes de
    // gastar el viaje, y con la palabra que entiende quien lo está leyendo.
    if (confirmOrder.payment_method === 'otro' && !ref) {
      setConfirmError('Con "Otro" hay que anotar la referencia: sin ella no se sabe qué se cobró.');
      return;
    }
    setConfirmBusy(true);
    setConfirmError('');
    const { data, error } = await supabase.rpc('admin_confirm_manual_payment', {
      p_order_id: confirmOrder.id,
      p_reference: ref || null,
    });
    setConfirmBusy(false);
    // Un 42501 del gate de admin, un PGRST202 si la 275 no está aplicada, o la
    // red. Se pinta el mensaje crudo: aquí no hay nada que traducir porque no
    // es una respuesta de la RPC, es que no se ha llegado a ella.
    if (error) {
      setConfirmError(`No se pudo confirmar el cobro: ${error.message}`);
      return;
    }
    const res = (data ?? {}) as {
      ok?: boolean;
      error?: string;
      status?: string;
      detalle?: string;
      entregado?: boolean;
    };
    if (!res.ok) {
      setConfirmError(mensajeDeFallo(res));
      // La lista se recarga IGUAL. 'ya_no_estaba_pendiente' significa que otro
      // lo cobró mientras esta pantalla miraba, y dejar la fila pintada como
      // pendiente sería repetir la mentira que este panel ya cometió una vez.
      await loadOrders();
      return;
    }
    // No se parchea la fila en memoria: la RPC mueve `status`, `prep_status`,
    // el intent, la referencia y quién confirmó, y en el camino del pedido sin
    // nada físico lo vuelve a mover a 'delivered'. Reconstruir eso aquí sería
    // mantener dos veces la misma lógica; se relee y punto.
    setConfirmOrder(null);
    setConfirmRef('');
    setFlash(
      res.entregado
        ? 'Cobro confirmado. El pedido no llevaba nada que preparar, así que queda entregado.'
        : 'Cobro confirmado. El pedido pasa a "Por preparar".'
    );
    await loadOrders();
  }

  const sinPagarTotal = orders.filter(sinPagar).length;
  // [275] Lo que falta por cobrar en mano. Es dinero pendiente de entrar, así
  // que se cuenta a la vista y no detrás de un filtro que haya que recordar.
  const enManoTotal = orders.filter(pendienteDeCobroEnMano).length;

  // [276] Buscar por número, además de por nombre. Es un O y no un Y: quien
  // teclea "42" quiere el pedido 42, no los pedidos de alguien que se llame 42.
  const numero = numeroBuscado(search);
  const filtered = orders.filter((o) => {
    const matchNombre = (o.profile?.full_name ?? o.billing_name ?? '')
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchSearch = matchNombre || (numero !== null && o.order_number === numero);
    // [259] Se compara contra el estado de COBRO colapsado: en la fase 1 la app
    // vieja todavía puede dejar un pedido en 'shipped'/'delivered', y para el
    // panel los tres son "Procesado". Sin colapsar, filtrar por "Procesado"
    // escondería los pedidos que el móvil ya movió.
    // [275] `cobro_en_mano` no es un valor de la columna: es "pending + una de
    // las cuatro formas en mano". Va en el mismo desplegable porque para quien
    // mira la tabla sí es un estado —lo que falta por cobrar— y ponerlo en un
    // segundo control lo escondería justo donde tiene que verse.
    const matchStatus =
      filtroStatus === 'todos'
        ? true
        : filtroStatus === 'cobro_en_mano'
          ? pendienteDeCobroEnMano(o)
          : cobroDe(o.status) === filtroStatus;
    // Solo se apartan en la vista "todos". Si alguien elige el estado a mano
    // ya ha dicho qué quiere ver, y esconderle las filas sería mentirle otra
    // vez con una tabla vacía.
    const oculto = filtroStatus === 'todos' && !verSinPagar && sinPagar(o);
    return matchSearch && matchStatus && !oculto;
  });

  return (
    <div className="space-y-6">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="pedidos-list-error"
        >
          {listError}
        </p>
      )}

      {bloquesFallidos.length > 0 && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="pedidos-bloques-fallidos"
        >
          No se ha podido leer {listaEnCastellano(bloquesFallidos)}. Los pedidos se ven, pero un
          regalo sin nombre no dice quién lo hizo y un cobro en mano sin nombre no dice quién lo dio
          por bueno.
        </p>
      )}

      {flash && (
        <p
          className="text-success text-sm border border-success/30 rounded px-3 py-2"
          data-testid="pedidos-flash"
        >
          {flash}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
          />
          <input
            className="input pl-9 h-9 text-sm"
            placeholder="Buscar por alumno..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input h-9 text-sm w-auto"
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="todos">Todos los estados</option>
          {/* [275] Lo que dirección tiene que cobrar. No es un estado de la
              columna, así que va suelto y no dentro del bucle de abajo. */}
          <option value="cobro_en_mano">Pendientes de cobro en mano</option>
          {/* [259] 'shipped' y 'delivered' salen del desplegable: ya no son
              estados de cobro, y filtrar por ellos dejaría fuera todo lo que el
              panel prepare a partir de ahora, que se queda en 'paid'. La
              preparación se lee en su propia insignia. */}
          {Object.entries(STATUS_LABELS)
            .filter(([v]) => v !== 'shipped' && v !== 'delivered')
            .map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
        </select>
        {/* Atajo al filtro, con la cuenta. Mismo papel que el contador de
            intentos sin pagar, pero al revés: aquellos se apartan de la vista y
            estos hay que ir a buscarlos. */}
        {enManoTotal > 0 && (
          <button
            type="button"
            data-testid="pedidos-en-mano-total"
            onClick={() => setFiltroStatus('cobro_en_mano')}
            className="badge-gold self-center whitespace-nowrap"
          >
            {enManoTotal} por cobrar en mano
          </button>
        )}
        <label className="flex items-center gap-2 text-sm text-text-secondary whitespace-nowrap">
          <input
            type="checkbox"
            data-testid="pedidos-ver-sin-pagar"
            checked={verSinPagar}
            onChange={(e) => setVerSinPagar(e.target.checked)}
          />
          Ver intentos sin pagar
          {sinPagarTotal > 0 && (
            <span data-testid="pedidos-sin-pagar-total" className="badge-silver">
              {sinPagarTotal}
            </span>
          )}
        </label>
      </div>

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center h-48 items-center">
            <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Pedido</th>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Productos</th>
                  <th className="table-th">Total</th>
                  <th className="table-th">Fecha</th>
                  <th className="table-th">Estado</th>
                  <th className="table-th">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="table-td text-center text-text-secondary py-12"
                      data-testid="pedidos-vacio"
                    >
                      {/* "Sin pedidos" a secas volvería a mentir justo donde ya
                          mintió con el embed ambiguo: si lo único que hay son
                          intentos sin pagar, hay que decirlo. */}
                      {loadFailed
                        ? 'No se pudieron cargar los pedidos'
                        : filtroStatus === 'cobro_en_mano'
                          ? 'No hay ningún pedido pendiente de cobro en mano.'
                          : sinPagarTotal > 0 && !verSinPagar
                            ? `Sin pedidos que preparar. Hay ${sinPagarTotal} ${
                                sinPagarTotal === 1 ? 'intento' : 'intentos'
                              } de compra sin pagar — marca "Ver intentos sin pagar" para verlos.`
                            : 'Sin pedidos'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((o) => (
                    <tr key={o.id} className="table-row">
                      <td className="table-td">
                        <span
                          data-testid={`pedido-numero-${o.id}`}
                          className="font-mono text-xs text-text-secondary whitespace-nowrap"
                        >
                          {numeroDePedido(o.order_number)}
                        </span>
                      </td>
                      <td className="table-td">
                        <p className="font-medium">
                          <TitularCuenta
                            userId={o.user_id}
                            fullName={o.profile?.full_name}
                            billingName={o.billing_name}
                          />
                        </p>
                        <p className="text-text-secondary text-xs">{o.profile?.email}</p>
                      </td>
                      <td className="table-td">
                        <div className="space-y-1">
                          {o.items?.map((item, i) => (
                            <p key={i} className="text-text-secondary text-xs">
                              {item.name} ×{item.quantity ?? item.qty}
                              {item.size ? ` · ${item.size}` : ''}
                              {item.color ? ` · ${item.color}` : ''}
                            </p>
                          ))}
                        </div>
                      </td>
                      <td className="table-td">
                        <span className="font-oswald text-gold-primary">
                          {(o.total_cents / 100).toFixed(2)}€
                        </span>
                        {/* Un pedido a 0 € ya no viene de un solo sitio. Hasta la
                            mig 246 sí —el canje de un código gratuito (mig 193)—
                            y por eso aquí ponía "Gratis" en todos. Ahora también
                            lo crea un regalo del club, y son cosas distintas:
                            una la decidió alguien con nombre. `gifted_by` es lo
                            que las separa; el importe no. */}
                        {o.gifted_by ? (
                          <span
                            data-testid={`pedido-regalo-${o.id}`}
                            className="ml-2 rounded bg-gold-primary/20 px-1.5 py-0.5 text-xs text-gold-primary"
                            title={
                              nameMap[o.gifted_by]
                                ? `Regalo de ${nameMap[o.gifted_by]}`
                                : 'Regalo del club'
                            }
                          >
                            Regalo
                          </span>
                        ) : (
                          o.total_cents === 0 && (
                            <span
                              data-testid={`pedido-gratis-${o.id}`}
                              className="ml-2 rounded bg-success/20 px-1.5 py-0.5 text-xs text-success"
                            >
                              Gratis
                            </span>
                          )
                        )}
                      </td>
                      <td className="table-td text-text-secondary text-xs">
                        {format(new Date(o.created_at), 'dd/MM/yyyy')}
                      </td>
                      <td className="table-td">
                        {/* [275] Un pedido en mano sin cobrar NO es "Sin pagar":
                            esa etiqueta es la del carrito abandonado y se lee
                            como "aquí no hay nada que hacer". Aquí hay justo lo
                            contrario, alguien a quien cobrar — así que ámbar,
                            como "Por preparar", que es la otra tarea del club. */}
                        {pendienteDeCobroEnMano(o) ? (
                          <span className="badge-gold" data-testid={`pedido-por-cobrar-${o.id}`}>
                            Pendiente de cobro
                          </span>
                        ) : (
                          <span className={STATUS_BADGE[cobroDe(o.status)] ?? 'badge-silver'}>
                            {STATUS_LABELS[cobroDe(o.status)] ?? o.status}
                          </span>
                        )}
                        {/* La forma de pago, solo cuando se cobra en mano: en un
                            pedido de tarjeta no dice nada que no se sepa ya, y
                            un regalo o un carrito gratuito ya llevan su propia
                            insignia en el importe. Sigue viéndose DESPUÉS de
                            cobrado, que es cuando hace falta para el arqueo. */}
                        {enMano(o) && (
                          <span
                            data-testid={`pedido-forma-pago-${o.id}`}
                            className="badge-silver ml-2"
                          >
                            {etiquetaDeLaFormaDePago(o.payment_method)}
                          </span>
                        )}
                        {/* [259] La preparación, en su propia insignia y solo
                            cuando hay algo que preparar. */}
                        {PREP_LABELS[o.prep_status] && (
                          <span
                            data-testid={`pedido-prep-${o.id}`}
                            className={`${PREP_BADGE[o.prep_status]} ml-2`}
                          >
                            {PREP_LABELS[o.prep_status]}
                          </span>
                        )}
                        {o.fulfillment_error && (
                          <span
                            data-testid={`pedido-sin-conceder-${o.id}`}
                            className="badge-error ml-2"
                            title={o.fulfillment_error}
                          >
                            Pagado sin conceder
                          </span>
                        )}
                      </td>
                      <td className="table-td">
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => setDetailOrder(o)}
                            className="btn-ghost flex items-center gap-1 text-xs px-2 py-1"
                          >
                            <Eye size={12} />
                            Ver
                          </button>
                          {/* [259] "Procesar" YA NO EXISTE. Nunca fue un paso de
                              preparación: escribía 'paid', que es la palabra
                              del candado —`fulfill_order` reclama el pedido con
                              `WHERE status='pending'`— y pulsarlo antes de que
                              llegara el webhook dejaba la función devolviendo
                              claimed:false sin conceder nada, en silencio. El
                              #497 le quitó la puerta escondiendo el botón sobre
                              un pedido sin pagar; esto le quita la habitación.

                              Un pedido llega a 'paid' cuando se cobra, y lo
                              hace `fulfill_order`. Lo único que el club hace a
                              mano es prepararlo, y eso son estos dos botones,
                              que escriben `prep_status`. */}
                          {/* [275] Confirmar el cobro. Es lo único de esta
                              página que llama a una RPC en vez de escribir la
                              tabla, y tiene que serlo: ver `confirmarCobro`. */}
                          {pendienteDeCobroEnMano(o) && (
                            <button
                              onClick={() => abrirConfirmacion(o)}
                              className="btn-ghost flex items-center gap-1 text-xs px-2 py-1 text-gold-primary"
                            >
                              <Banknote size={12} />
                              Confirmar cobro
                            </button>
                          )}
                          {o.prep_status === 'pendiente' && (
                            <button
                              onClick={() => updatePrep(o, 'enviado')}
                              className="btn-ghost flex items-center gap-1 text-xs px-2 py-1"
                            >
                              <Truck size={12} />
                              Enviar
                            </button>
                          )}
                          {o.prep_status === 'enviado' && (
                            <button
                              onClick={() => updatePrep(o, 'entregado')}
                              className="btn-ghost flex items-center gap-1 text-xs px-2 py-1"
                            >
                              <CheckCircle size={12} />
                              Entregado
                            </button>
                          )}
                          {/* Cancelar SÍ es un cambio de cobro, así que sigue
                              escribiendo `status`. Y sigue sin existir para un
                              pedido sin pagar: ahí tampoco hay nada que anular,
                              y moverlo quemaría el candado igual. */}
                          {(cobroDe(o.status) === 'pending' || cobroDe(o.status) === 'paid') &&
                            !sinPagar(o) && (
                              <button
                                data-testid={`pedido-cancelar-${o.id}`}
                                onClick={() => updateStatus(o, 'cancelled')}
                                className="btn-ghost flex items-center gap-1 text-xs px-2 py-1 text-error hover:text-error"
                              >
                                <XCircle size={12} />
                                {/* [317] El rótulo dice lo que va a pasar: uno
                                    cobrado abre el paso previo, uno sin cobrar
                                    se anula y no hay nada que devolver. */}
                                {cobroDe(o.status) === 'paid' ? 'Anular y revocar' : 'Cancelar'}
                              </button>
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal detalle pedido */}
      {/* [276] El número en el título: es lo que dirección tiene delante cuando
          está cotejando una transferencia con un pedido. */}
      <Modal
        open={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        title={
          detailOrder ? `Pedido ${numeroDePedido(detailOrder.order_number)}` : 'Detalle del pedido'
        }
      >
        {detailOrder && (
          <div className="space-y-5">
            {/* Alumno */}
            <div>
              <p className="label mb-1">Cliente</p>
              <div className="p-3 rounded-lg bg-bg-elevated border border-bg-border space-y-1">
                <p className="text-white font-medium">
                  <TitularCuenta
                    userId={detailOrder.user_id}
                    fullName={detailOrder.profile?.full_name}
                    billingName={detailOrder.billing_name}
                  />
                </p>
                <p className="text-text-secondary text-sm">{detailOrder.profile?.email}</p>
                {detailOrder.profile?.phone && (
                  <p className="text-text-secondary text-sm">{detailOrder.profile.phone}</p>
                )}
              </div>
            </div>

            {/* Dirección */}
            {detailOrder.shipping_address && (
              <div>
                <p className="label mb-1 flex items-center gap-1">
                  <MapPin size={12} />
                  Dirección de envío
                </p>
                <div className="p-3 rounded-lg bg-bg-elevated border border-bg-border text-sm text-text-secondary space-y-0.5">
                  {detailOrder.shipping_address.street && (
                    <p>{detailOrder.shipping_address.street}</p>
                  )}
                  {detailOrder.shipping_address.city && (
                    <p>
                      {detailOrder.shipping_address.city}
                      {detailOrder.shipping_address.postal_code
                        ? `, ${detailOrder.shipping_address.postal_code}`
                        : ''}
                    </p>
                  )}
                  {detailOrder.shipping_address.country && (
                    <p>{detailOrder.shipping_address.country}</p>
                  )}
                  {!detailOrder.shipping_address.street && (
                    <p>{JSON.stringify(detailOrder.shipping_address)}</p>
                  )}
                </div>
              </div>
            )}

            {/* Productos */}
            <div>
              <p className="label mb-1">Productos</p>
              <div className="rounded-lg border border-bg-border overflow-hidden">
                {detailOrder.items?.map((item, i) => {
                  const eco = lineEconomics(item);
                  const typeLabel = itemTypeLabel(item);
                  const code = item.applied_code_id ? codeMap[item.applied_code_id] : null;
                  return (
                    <div
                      key={i}
                      data-testid={`pedido-item-${i}`}
                      className="flex justify-between items-start p-3 border-b border-bg-border last:border-0 bg-bg-elevated"
                    >
                      <div>
                        <p className="text-white text-sm font-medium">
                          {item.name}
                          {typeLabel && (
                            <span className="ml-2 text-text-secondary text-xs font-normal">
                              · {typeLabel}
                            </span>
                          )}
                        </p>
                        <div className="flex gap-3 mt-0.5 flex-wrap">
                          <p className="text-text-secondary text-xs">Cantidad: {eco.qty}</p>
                          <p className="text-text-secondary text-xs">
                            Precio unitario:{' '}
                            <span className="text-white">{(eco.unit / 100).toFixed(2)}€</span>
                          </p>
                          {item.size && (
                            <p className="text-text-secondary text-xs">
                              Talla: <span className="text-white">{item.size}</span>
                            </p>
                          )}
                          {item.color && (
                            <p className="text-text-secondary text-xs">
                              Color: <span className="text-white">{item.color}</span>
                            </p>
                          )}
                        </div>
                        {componentesV2(item).length > 0 && (
                          <ul
                            data-testid={`pedido-item-${i}-componentes`}
                            className="mt-1 ml-3 border-l border-white/10 pl-3 space-y-0.5"
                          >
                            {componentesV2(item).map((c, j) => (
                              <li key={j} className="text-text-secondary text-xs">
                                {c.quantity && c.quantity > 1 ? `${c.quantity}× ` : ''}
                                {c.name ?? 'Artículo'}
                                {c.variant_label || c.size || c.color ? (
                                  <span className="text-white">
                                    {' · '}
                                    {c.variant_label ??
                                      [c.size, c.color].filter(Boolean).join(' · ')}
                                  </span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                        {eco.discount > 0 && (
                          <p
                            data-testid={`pedido-item-${i}-descuento`}
                            className="text-[#A3E635] text-xs mt-0.5"
                          >
                            Descuento{code ? ` ${code}` : ''}: −{(eco.discount / 100).toFixed(2)}€
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {eco.discount > 0 && (
                          <p className="text-text-secondary text-xs line-through">
                            {(eco.lineBase / 100).toFixed(2)}€
                          </p>
                        )}
                        <p
                          data-testid={`pedido-item-${i}-final`}
                          className="text-gold-primary font-oswald"
                        >
                          {(eco.lineFinal / 100).toFixed(2)}€
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-between p-3 bg-bg-primary">
                  <p className="text-text-secondary text-sm">Total</p>
                  <p className="text-gold-primary font-oswald text-lg">
                    {(detailOrder.total_cents / 100).toFixed(2)}€
                  </p>
                </div>
              </div>
            </div>

            {/* [248] Un regalo del club, con nombre y apellidos. Un pedido a
                0 € sin esto no dice nada: ni si alguien lo decidió ni por qué,
                y esa pregunta llega semanas después, cuando ya nadie se acuerda.
                El motivo es opcional, así que se dice también cuando falta. */}
            {detailOrder.gifted_by && (
              <div
                data-testid="pedido-detalle-regalo"
                className="border border-gold-primary/30 rounded px-3 py-2"
              >
                <p className="text-gold-primary text-sm font-semibold">Regalo del club</p>
                <p className="mt-1 text-xs text-text-secondary">
                  Lo regaló{' '}
                  <strong className="text-white">
                    {nameMap[detailOrder.gifted_by] ?? 'alguien del club'}
                  </strong>
                  {detailOrder.gift_reason ? (
                    <>
                      {' · '}
                      <span className="text-white">{detailOrder.gift_reason}</span>
                    </>
                  ) : (
                    ' · sin motivo anotado'
                  )}
                  . No se cobró nada.
                </p>
              </div>
            )}

            {/* [229] Un pedido cobrado que no concedió nada. Va ARRIBA del
                estado y con el motivo entero: el operador tiene que poder
                arreglarlo sin abrir la base. */}
            {detailOrder.fulfillment_error && (
              <div
                data-testid="pedido-detalle-sin-conceder"
                className="border border-error/30 rounded px-3 py-2"
              >
                <p className="text-error text-sm font-semibold">
                  Pagado, pero no se le ha concedido nada
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  El cobro está hecho y no se ha deshecho a propósito. Hay que darle lo comprado a
                  mano. El motivo de abajo dice si falló un producto mal configurado (229) o si el
                  pago llegó con el pedido fuera de «pendiente» (BUG 15).
                </p>
                <p className="mt-2 break-words font-mono text-xs text-text-secondary">
                  {detailOrder.fulfillment_error}
                </p>
              </div>
            )}

            {/* [275] Cómo se cobró, quién lo dio por bueno y cuándo. Es la
                pregunta del arqueo, y hasta ahora había que deducirla del
                prefijo de un intent —o no se podía deducir en absoluto. */}
            <div>
              <p className="label mb-1">Forma de pago</p>
              <div
                data-testid="pedido-detalle-pago"
                className="p-3 rounded-lg bg-bg-elevated border border-bg-border space-y-1 text-sm"
              >
                <p className="text-white">
                  {etiquetaDeLaFormaDePago(detailOrder.payment_method)}
                </p>
                {detailOrder.payment_reference && (
                  <p className="text-text-secondary text-xs">
                    Referencia: <span className="text-white">{detailOrder.payment_reference}</span>
                  </p>
                )}
                {detailOrder.paid_confirmed_at && (
                  <p className="text-text-secondary text-xs">
                    Cobro confirmado por{' '}
                    <span className="text-white">
                      {(detailOrder.paid_confirmed_by && nameMap[detailOrder.paid_confirmed_by]) ??
                        'alguien del club'}
                    </span>{' '}
                    el {format(new Date(detailOrder.paid_confirmed_at), 'dd/MM/yyyy HH:mm')}
                  </p>
                )}
                {pendienteDeCobroEnMano(detailOrder) && (
                  <p className="text-text-secondary text-xs">
                    Todavía sin cobrar. El pedido no caduca y no se le ha concedido nada: lo
                    comprado se le concede al confirmar el cobro.
                  </p>
                )}
              </div>
            </div>

            {/* Estado + acciones */}
            <div>
              <p className="label mb-2">Estado actual</p>
              <div className="flex items-center gap-3 flex-wrap">
                <span className={STATUS_BADGE[cobroDe(detailOrder.status)] ?? 'badge-silver'}>
                  {STATUS_LABELS[cobroDe(detailOrder.status)] ?? detailOrder.status}
                </span>
                {PREP_LABELS[detailOrder.prep_status] && (
                  <span
                    data-testid="pedido-detalle-prep"
                    className={PREP_BADGE[detailOrder.prep_status]}
                  >
                    {PREP_LABELS[detailOrder.prep_status]}
                  </span>
                )}
                <div className="flex gap-2 flex-wrap">
                  {/* [259] Aquí tampoco hay "Procesar": ver la nota de la lista. */}
                  {pendienteDeCobroEnMano(detailOrder) && (
                    <button
                      onClick={() => abrirConfirmacion(detailOrder)}
                      className="btn-gold flex items-center gap-1 text-sm px-3 py-1.5"
                    >
                      <Banknote size={14} />
                      Confirmar cobro
                    </button>
                  )}
                  {detailOrder.prep_status === 'pendiente' && (
                    <button
                      onClick={() => updatePrep(detailOrder, 'enviado')}
                      className="btn-gold flex items-center gap-1 text-sm px-3 py-1.5"
                    >
                      <Truck size={14} />
                      Marcar enviado
                    </button>
                  )}
                  {detailOrder.prep_status === 'enviado' && (
                    <button
                      onClick={() => updatePrep(detailOrder, 'entregado')}
                      className="btn-gold flex items-center gap-1 text-sm px-3 py-1.5"
                    >
                      <CheckCircle size={14} />
                      Marcar entregado
                    </button>
                  )}
                  {(cobroDe(detailOrder.status) === 'pending' ||
                    cobroDe(detailOrder.status) === 'paid') &&
                    !sinPagar(detailOrder) && (
                      <button
                        data-testid="pedido-detalle-cancelar"
                        onClick={() => updateStatus(detailOrder, 'cancelled')}
                        className="btn-ghost flex items-center gap-1 text-sm px-3 py-1.5 text-error"
                      >
                        <XCircle size={14} />
                        {cobroDe(detailOrder.status) === 'paid' ? 'Anular y revocar' : 'Cancelar'}
                      </button>
                    )}
                </div>
              </div>

              {/* EL HUECO DE ERROR DEL DETALLE.
                  Todo lo que se pulsa aquí dentro —«Marcar enviado», «Marcar
                  entregado», «Cancelar»— manda su fallo a `listError`, y ese
                  banner se pinta en lo alto de la PÁGINA, debajo de este modal.
                  Con el modal abierto no se ve ninguno, así que un rechazo de la
                  base se leía como «el botón no hace nada». Jose lo encontró así
                  en producción: «existe y NO HACE NADA. Sin error, sin mensaje».

                  Es el MISMO estado, pintado también aquí, y no uno nuevo: dos
                  variables para un mismo error acaban discrepando, y además así
                  el mensaje sigue estando cuando se cierra el modal. Solo uno de
                  los dos se ve a la vez, porque el modal tapa la página. */}
              {listError && (
                <p
                  data-testid="pedido-detalle-error"
                  className="text-error text-sm border border-error/30 rounded px-3 py-2 mt-3"
                >
                  {listError}
                </p>
              )}

              {sinPagar(detailOrder) && (
                <p
                  data-testid="pedido-sin-pagar-explicacion"
                  className="text-text-secondary text-xs mt-2 border border-bg-border rounded px-3 py-2"
                >
                  Este pedido no se ha pagado: se creó al pulsar &quot;Pagar&quot; y el pago nunca
                  llegó a Stripe. No hay nada que preparar. No se procesa ni se cancela a mano — si
                  la familia terminase de pagar, tocarlo aquí impediría que se le concediera lo
                  comprado.
                </p>
              )}
              <p className="text-text-secondary text-xs mt-2">
                Pedido del {format(new Date(detailOrder.created_at), 'dd/MM/yyyy HH:mm')}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* [317] REVOCAR LO CONCEDIDO. Dos pasos, y el primero es la lista: esto
          mueve dinero y material, y quien lo pulsa tiene que ver qué se va a
          retirar antes de retirarlo. La lista la da la propia función que
          revoca (con `aplicar` a false), no esta pantalla. */}
      <Modal
        open={!!revokeOrder}
        onClose={() => setRevokeOrder(null)}
        title="Anular y revocar lo concedido"
        size="sm"
      >
        {revokeOrder && (
          <div className="space-y-4" data-testid="revocar-dialogo">
            <p className="text-text-secondary text-sm">
              Vas a anular el pedido{' '}
              <strong className="text-white">#{revokeOrder.order_number}</strong> de{' '}
              <strong className="text-white">
                <TitularCuenta
                  userId={revokeOrder.user_id}
                  fullName={revokeOrder.profile?.full_name}
                  billingName={revokeOrder.billing_name}
                />
              </strong>{' '}
              y retirar lo que le concedió.
            </p>

            {!revokeGrants && !revokeError && (
              <p className="text-text-secondary text-sm" data-testid="revocar-cargando">
                Leyendo lo que concedió…
              </p>
            )}

            {revokeGrants && (
              <ul className="text-sm space-y-1" data-testid="revocar-lista">
                <li className="text-white">
                  Suscripciones: {revokeGrants.suscripciones?.length ?? 0}
                  {(revokeGrants.suscripciones?.length ?? 0) > 0 && (
                    <span className="text-text-secondary"> — se liberan sus sesiones futuras</span>
                  )}
                </li>
                <li className="text-white">
                  Bonos: {revokeGrants.bonos?.length ?? 0}
                  {(revokeGrants.bonos ?? []).some((b) => b.gastadas > 0) && (
                    <span className="text-warning"> — alguno tiene sesiones ya gastadas</span>
                  )}
                </li>
                <li className="text-white">
                  Créditos de vídeo: {revokeGrants.creditos_video?.length ?? 0}
                </li>
                <li className="text-white">
                  Vuelven al inventario:{' '}
                  {(revokeGrants.stock ?? []).reduce((n, x) => n + x.unidades, 0)} unidad(es)
                </li>
                {/* [328] Y lo que NO vuelve, cuando el envío ya salió. Antes esta
                    lista no podía decirlo porque el pedido ni llegaba aquí: se
                    rechazaba entero. Ahora se revoca lo revocable y el material
                    se queda donde está, así que hay que decirlo ANTES de
                    confirmar — y decir quién lo arregla. */}
                {(revokeGrants.material_no_devuelto ?? []).length > 0 && (
                  <li className="text-warning" data-testid="revocar-material-fuera">
                    NO vuelven al inventario:{' '}
                    {(revokeGrants.material_no_devuelto ?? []).reduce(
                      (n, x) => n + x.unidades,
                      0
                    )}{' '}
                    unidad(es) — el envío ya se entregó. Ajusta el inventario a mano si lo
                    recuperas.
                  </li>
                )}
                <li className="text-white">
                  Usos de código devueltos: {revokeGrants.descuentos_liberados ?? 0}
                </li>
                <li className="text-white">
                  Envío:{' '}
                  {revokeGrants.envio === 'pendiente' ? 'se corta' : (revokeGrants.envio ?? '—')}
                </li>
                {/* Lo que NO se toca, dicho aquí y no solo en el aviso al
                    jugador: la matrícula la puede haber concedido también otro
                    pedido pagado, así que quitarla dejaría sin ella a quien sí
                    la pagó. */}
                <li className="text-text-secondary">
                  La matrícula anual NO se retira: puede venir de otro pedido pagado.
                </li>
              </ul>
            )}

            <div>
              <label className="label mb-1 block" htmlFor="revocar-motivo">
                Motivo (opcional)
              </label>
              <input
                id="revocar-motivo"
                data-testid="revocar-motivo"
                className="input"
                value={revokeMotivo}
                onChange={(e) => setRevokeMotivo(e.target.value)}
                placeholder="Lo que le llegará al jugador en el aviso"
              />
            </div>

            {revokeError && (
              <p
                data-testid="revocar-error"
                className="text-error text-sm border border-error/30 rounded px-3 py-2"
              >
                {revokeError}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevokeOrder(null)}
                className="btn-ghost"
                disabled={revokeBusy}
              >
                Volver
              </button>
              <button
                onClick={revocar}
                className="btn-gold"
                disabled={revokeBusy || !revokeGrants}
                data-testid="revocar-confirmar"
              >
                {revokeBusy ? 'Revocando...' : 'Anular y revocar'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* [275] Confirmar un cobro en mano. Un diálogo y no un `confirm()` del
          navegador por dos razones: hay una referencia que escribir, y esto
          mueve dinero — quien lo pulsa tiene que ver a quién le está cobrando
          cuánto antes de darle. */}
      <Modal
        open={!!confirmOrder}
        onClose={() => setConfirmOrder(null)}
        title="Confirmar cobro"
        size="sm"
      >
        {confirmOrder && (
          <div className="space-y-4">
            <p className="text-text-secondary text-sm">
              Vas a dar por cobrado el pedido de{' '}
              <strong className="text-white">
                <TitularCuenta
                  userId={confirmOrder.user_id}
                  fullName={confirmOrder.profile?.full_name}
                  billingName={confirmOrder.billing_name}
                />
              </strong>{' '}
              por{' '}
              <strong className="text-gold-primary">
                {(confirmOrder.total_cents / 100).toFixed(2)}€
              </strong>{' '}
              en{' '}
              <strong className="text-white">
                {(
                  etiquetaDeLaFormaDePago(confirmOrder.payment_method)
                ).toLowerCase()}
              </strong>
              . A partir de ahí se le concede lo comprado, igual que si hubiera pagado con tarjeta.
            </p>

            <div>
              <label className="label mb-1 block" htmlFor="cobro-referencia">
                Referencia{confirmOrder.payment_method === 'otro' ? '' : ' (opcional)'}
              </label>
              <input
                id="cobro-referencia"
                data-testid="cobro-referencia"
                className="input"
                value={confirmRef}
                onChange={(e) => setConfirmRef(e.target.value)}
                placeholder={
                  confirmOrder.payment_method === 'transferencia'
                    ? 'Nº de la transferencia'
                    : 'Lo que quede anotado del cobro'
                }
              />
              {confirmOrder.payment_method === 'otro' && (
                <p className="text-text-secondary text-xs mt-1">
                  Con &quot;Otro&quot; es obligatoria: sin ella no se sabe qué se cobró.
                </p>
              )}
            </div>

            {confirmError && (
              <p
                data-testid="cobro-error"
                className="text-error text-sm border border-error/30 rounded px-3 py-2"
              >
                {confirmError}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOrder(null)}
                className="btn-ghost"
                disabled={confirmBusy}
              >
                Volver
              </button>
              <button
                onClick={confirmarCobro}
                className="btn-gold"
                disabled={confirmBusy}
                data-testid="cobro-confirmar"
              >
                {confirmBusy ? 'Confirmando...' : 'Confirmar cobro'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
